import { type ReactNode } from 'react';
import { usePermissions } from '@/hooks/use-permissions';

interface IfPermittedProps {
  /** Single permission required to render the children. */
  permission?: string;
  /** Render the children when *any* of these permissions is held. */
  permissionAny?: string[];
  /** Render the children when *all* of these permissions are held. */
  permissionAll?: string[];
  /** Rendered when the user lacks the required permission(s). Defaults to `null`. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Inline render-gate for permission-bound UI. Renders `children` only when the
 * current user holds the required permission(s); otherwise renders `fallback`
 * (default `null`). Use this for buttons, menu items, panels — anything that
 * should silently disappear rather than show a 403.
 *
 * Admins implicitly hold every permission (handled by `usePermissions`).
 *
 * For route-level gates that should show a 403 page, use `RequirePermission`
 * from `shell/components/require-permission.component.tsx` instead.
 *
 * @example
 *   <IfPermitted permission={PERMISSIONS.AI_ASSISTANT_VIEW}>
 *     <AssistantButton />
 *   </IfPermitted>
 *
 *   <IfPermitted permissionAny={[PERMISSIONS.ITEMS_EDIT, PERMISSIONS.ITEMS_EDIT_PRICES]}>
 *     <EditButton />
 *   </IfPermitted>
 */
export function IfPermitted({
  permission,
  permissionAny,
  permissionAll,
  fallback = null,
  children,
}: IfPermittedProps) {
  const { has, hasAny } = usePermissions();

  // No constraint configured → behave like an inert pass-through. Catches the
  // case where a caller forgets to pass any permission prop; surfacing an
  // error in production would be noisier than just rendering the children.
  if (!permission && !permissionAny && !permissionAll) {
    return <>{children}</>;
  }

  const allowed =
    (permission == null || has(permission)) &&
    (permissionAny == null || hasAny(permissionAny)) &&
    (permissionAll == null || permissionAll.every((p) => has(p)));

  return <>{allowed ? children : fallback}</>;
}
